// src/features/admin/pages/AdminBookStoresPage.tsx
// 구매처 마스터 관리. 삭제는 사용 건수 0일 때만 열어둔다 —
// user_books.store_id 가 ON DELETE SET NULL 이라 쓰이는 구매처를 지우면
// 그걸 고른 서재 기록의 구매처가 전부 조용히 사라진다. 목록에서 내릴 때는 비활성화를 쓸 것.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAdminRole } from "../AdminGuard";
import { COLOR, FW, BORDER, RADIUS } from "@/ui/tokens";

type Store = {
    id: number;
    code: string;
    name: string;
    logo_url: string | null;
    sort_order: number;
    is_active: boolean;
    is_primary: boolean;
    android_package: string | null;
    android_package_eink: string | null;
    usage_count: number;
};

// 목록에 한 줄로 요약. 둘 다 채워져 있으면 앱에서 칩을 눌렀을 때 선택 시트가 뜬다.
const pkgSummary = (s: Store) => {
    const parts: string[] = [];
    if (s.android_package) parts.push(s.android_package);
    if (s.android_package_eink) parts.push(`e-ink: ${s.android_package_eink}`);
    return parts.length ? parts.join("  ·  ") : "앱 연동 없음";
};

const ERROR_TEXT: Record<string, string> = {
    DUPLICATE_CODE: "이미 사용 중인 코드입니다.",
    INVALID_CODE: "코드는 영문 대문자, 숫자, _ 만 쓸 수 있습니다.",
    CODE_REQUIRED: "코드를 입력하세요.",
    CODE_TOO_LONG: "코드는 30자 이내여야 합니다.",
    NAME_REQUIRED: "이름을 입력하세요.",
    NAME_TOO_LONG: "이름은 50자 이내여야 합니다.",
    INVALID_SORT_ORDER: "순서는 0 이상의 정수여야 합니다.",
    INVALID_ANDROID_PACKAGE: "패키지명 형식이 올바르지 않습니다. (예: com.inno.pagebox)",
    ANDROID_PACKAGE_TOO_LONG: "패키지명은 255자 이내여야 합니다.",
    INVALID_ANDROID_PACKAGE_EINK: "e-ink 패키지명 형식이 올바르지 않습니다. (예: kr.co.millie.eink)",
    ANDROID_PACKAGE_EINK_TOO_LONG: "e-ink 패키지명은 255자 이내여야 합니다.",
};

const msg = (e: any, fallback: string) =>
    (e?.code && ERROR_TEXT[e.code]) || fallback;

export default function AdminBookStoresPage() {
    const { roles } = useAdminRole();
    const nav = useNavigate();
    useEffect(() => {
        if (roles.length > 0 && !roles.some(r => r === "ADMIN" || r === "SUPER_ADMIN")) {
            nav("/admin/tox", { replace: true });
        }
    }, [roles, nav]);

    const [stores, setStores] = useState<Store[]>([]);
    const [unassigned, setUnassigned] = useState(0);
    const [loading, setLoading] = useState(true);
    const [newCode, setNewCode] = useState("");
    const [newName, setNewName] = useState("");
    const [adding, setAdding] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [editName, setEditName] = useState("");
    const [editOrder, setEditOrder] = useState("");
    const [editPkg, setEditPkg] = useState("");
    const [editPkgEink, setEditPkgEink] = useState("");
    const [error, setError] = useState("");
    const editInputRef = useRef<HTMLInputElement>(null);

    const load = () => {
        setLoading(true);
        api("/admin/book-stores")
            .then(d => {
                setStores(d.stores || []);
                setUnassigned(d.unassigned_count || 0);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    useEffect(() => {
        if (editId !== null) editInputRef.current?.focus();
    }, [editId]);

    const add = async () => {
        const code = newCode.trim().toUpperCase();
        const name = newName.trim();
        if (!code || !name) return;
        setAdding(true);
        setError("");
        try {
            const row = await api("/admin/book-stores", { method: "POST", json: { code, name } });
            // sort_order 는 서버가 맨 뒤로 잡아주므로 정렬을 다시 맞춘다.
            setStores(prev => [...prev, row].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id));
            setNewCode("");
            setNewName("");
        } catch (e: any) {
            setError(msg(e, "추가 실패"));
        } finally {
            setAdding(false);
        }
    };

    const startEdit = (s: Store) => {
        setEditId(s.id);
        setEditName(s.name);
        setEditOrder(String(s.sort_order));
        setEditPkg(s.android_package ?? "");
        setEditPkgEink(s.android_package_eink ?? "");
        setError("");
    };

    const saveEdit = async (id: number) => {
        const name = editName.trim();
        const sort_order = Number(editOrder);
        if (!name) return;
        if (!Number.isInteger(sort_order) || sort_order < 0) {
            setError(ERROR_TEXT.INVALID_SORT_ORDER);
            return;
        }
        setError("");
        try {
            const row = await api(`/admin/book-stores/${id}`, {
                method: "PATCH",
                // 빈 문자열은 서버가 null(연동 앱 없음)로 저장한다.
                json: {
                    name, sort_order,
                    android_package: editPkg.trim(),
                    android_package_eink: editPkgEink.trim(),
                },
            });
            setStores(prev => prev
                .map(s => (s.id === id
                    ? {
                        ...s, name: row.name, sort_order: row.sort_order,
                        android_package: row.android_package,
                        android_package_eink: row.android_package_eink,
                    }
                    : s))
                .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id));
            setEditId(null);
        } catch (e: any) {
            setError(msg(e, "수정 실패"));
        }
    };

    const toggleActive = async (s: Store) => {
        const next = !s.is_active;
        if (!next && s.usage_count > 0) {
            const ok = confirm(
                `"${s.name}"은(는) 서재 기록 ${s.usage_count}건에서 쓰이고 있습니다.\n\n` +
                `비활성화하면 앞으로 새로 고를 수 없게 되지만, 이미 기록된 ${s.usage_count}건은 그대로 표시됩니다.\n계속할까요?`
            );
            if (!ok) return;
        }
        setError("");
        try {
            const row = await api(`/admin/book-stores/${s.id}`, { method: "PATCH", json: { is_active: next } });
            setStores(prev => prev.map(x => (x.id === s.id ? { ...x, is_active: row.is_active } : x)));
        } catch (e: any) {
            setError(msg(e, next ? "활성화 실패" : "비활성화 실패"));
        }
    };

    // 메인 ↔ 더보기. 앱의 구매처 선택 시트에서 기본 노출될지 "더보기" 뒤에 숨을지만 정한다.
    const togglePrimary = async (s: Store) => {
        const next = !s.is_primary;
        setError("");
        try {
            const row = await api(`/admin/book-stores/${s.id}`, { method: "PATCH", json: { is_primary: next } });
            setStores(prev => prev.map(x => (x.id === s.id ? { ...x, is_primary: row.is_primary } : x)));
        } catch (e: any) {
            setError(msg(e, next ? "메인으로 이동 실패" : "더보기로 이동 실패"));
        }
    };

    const remove = async (s: Store) => {
        if (s.usage_count > 0) return; // 버튼이 비활성화돼 있지만 방어적으로 한 번 더
        if (!confirm(`"${s.name}" 구매처를 완전히 삭제할까요? 되돌릴 수 없습니다.`)) return;
        setError("");
        try {
            await api(`/admin/book-stores/${s.id}`, { method: "DELETE" });
            setStores(prev => prev.filter(x => x.id !== s.id));
        } catch (e: any) {
            if (e?.code === "STORE_IN_USE") {
                setError("사용 중인 구매처는 삭제할 수 없습니다. 비활성화를 사용하세요.");
                load(); // 다른 관리자가 그 사이에 등록했을 수 있으니 목록을 새로 받는다
            } else {
                setError("삭제 실패");
            }
        }
    };

    const activeCount = stores.filter(s => s.is_active).length;
    // 그룹 안 정렬은 서버가 준 sort_order 순 그대로다(GET이 이미 정렬해서 준다).
    const primaryStores = stores.filter(s => s.is_primary);
    const moreStores = stores.filter(s => !s.is_primary);

    const renderRow = (s: Store, isLast: boolean) => (
                        <li
                            key={s.id}
                            style={{
                                display: "flex", alignItems: "center", gap: 12,
                                padding: "12px 22px",
                                borderBottom: isLast ? "none" : "1px solid #f4f4f5",
                                background: s.is_active ? "transparent" : "#fafafa",
                            }}
                        >
                            {/* sort_order */}
                            {editId === s.id ? (
                                <input
                                    value={editOrder}
                                    onChange={e => setEditOrder(e.target.value.replace(/[^0-9]/g, ""))}
                                    onKeyDown={e => {
                                        if (e.key === "Enter") saveEdit(s.id);
                                        if (e.key === "Escape") setEditId(null);
                                    }}
                                    style={{
                                        width: 52, padding: "5px 8px", fontSize: 12, textAlign: "center",
                                        border: "1px solid #a1a1aa", borderRadius: RADIUS.xs, outline: "none", flexShrink: 0,
                                    }}
                                />
                            ) : (
                                <span style={{
                                    minWidth: 40, height: 24, borderRadius: 12, padding: "0 8px",
                                    background: COLOR.bgLight, fontSize: 11, fontWeight: FW.bold,
                                    color: COLOR.textMuted, display: "flex", alignItems: "center",
                                    justifyContent: "center", flexShrink: 0,
                                }}>
                                    {s.sort_order}
                                </span>
                            )}

                            {/* name + 연동 앱 패키지명 (두 줄) */}
                            {editId === s.id ? (
                                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                                    <input
                                        ref={editInputRef}
                                        value={editName}
                                        onChange={e => setEditName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === "Enter") saveEdit(s.id);
                                            if (e.key === "Escape") setEditId(null);
                                        }}
                                        maxLength={50}
                                        style={{
                                            padding: "5px 10px", fontSize: 14,
                                            border: "1px solid #a1a1aa", borderRadius: RADIUS.xs, outline: "none",
                                        }}
                                    />
                                    <input
                                        value={editPkg}
                                        onChange={e => setEditPkg(e.target.value.trim())}
                                        onKeyDown={e => {
                                            if (e.key === "Enter") saveEdit(s.id);
                                            if (e.key === "Escape") setEditId(null);
                                        }}
                                        placeholder="연동 앱 패키지명 (없으면 비워두세요)"
                                        maxLength={255}
                                        style={{
                                            padding: "5px 10px", fontSize: 12,
                                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                            border: "1px solid #a1a1aa", borderRadius: RADIUS.xs, outline: "none",
                                        }}
                                    />
                                    {/* 두 칸이 다 차면 앱에서 칩을 눌렀을 때 일반/e-ink 선택 시트가 뜬다.
                                        한 칸만 차 있으면 선택 없이 그 앱을 바로 연다. */}
                                    <input
                                        value={editPkgEink}
                                        onChange={e => setEditPkgEink(e.target.value.trim())}
                                        onKeyDown={e => {
                                            if (e.key === "Enter") saveEdit(s.id);
                                            if (e.key === "Escape") setEditId(null);
                                        }}
                                        placeholder="e-ink 전용 앱 패키지명 (없으면 비워두세요)"
                                        maxLength={255}
                                        style={{
                                            padding: "5px 10px", fontSize: 12,
                                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                            border: "1px solid #a1a1aa", borderRadius: RADIUS.xs, outline: "none",
                                        }}
                                    />
                                </div>
                            ) : (
                                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                                    <span style={{
                                        fontSize: 14,
                                        color: s.is_active ? COLOR.textDark : COLOR.textLight,
                                        textDecoration: s.is_active ? "none" : "line-through",
                                    }}>
                                        {s.name}
                                    </span>
                                    <span style={{
                                        fontSize: 11, color: COLOR.textLight,
                                        fontFamily: (s.android_package || s.android_package_eink)
                                            ? "ui-monospace, SFMono-Regular, Menlo, monospace"
                                            : undefined,
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    }}>
                                        {pkgSummary(s)}
                                    </span>
                                </div>
                            )}

                            {/* code (읽기 전용) */}
                            <span style={{
                                fontSize: 11, color: COLOR.textLight, flexShrink: 0,
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                width: 130, textAlign: "right",
                            }}>
                                {s.code}
                            </span>

                            {/* 사용 건수 */}
                            <span style={{
                                fontSize: 12, flexShrink: 0, width: 70, textAlign: "right",
                                color: s.usage_count > 0 ? COLOR.textDark : COLOR.textLight,
                                fontWeight: s.usage_count > 0 ? FW.bold : FW.medium,
                            }}>
                                {s.usage_count > 0 ? `${s.usage_count}건` : "미사용"}
                            </span>

                            {/* 상태 */}
                            <span style={{
                                fontSize: 11, fontWeight: FW.bold, flexShrink: 0,
                                width: 48, textAlign: "center", padding: "3px 0", borderRadius: RADIUS.xs,
                                background: s.is_active ? "#dcfce7" : "#f4f4f5",
                                color: s.is_active ? "#15803d" : COLOR.textLight,
                            }}>
                                {s.is_active ? "활성" : "비활성"}
                            </span>

                            {/* Action buttons */}
                            {editId === s.id ? (
                                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                    <Btn color={COLOR.textDark} onClick={() => saveEdit(s.id)}>저장</Btn>
                                    <Btn color={COLOR.textMuted} onClick={() => setEditId(null)}>취소</Btn>
                                </div>
                            ) : (
                                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                    <Btn color="#2563eb" onClick={() => startEdit(s)}>수정</Btn>
                                    <Btn color="#7c3aed" onClick={() => togglePrimary(s)}>
                                        {s.is_primary ? "더보기로" : "메인으로"}
                                    </Btn>
                                    <Btn
                                        color={s.is_active ? "#b45309" : "#15803d"}
                                        onClick={() => toggleActive(s)}
                                    >
                                        {s.is_active ? "비활성화" : "활성화"}
                                    </Btn>
                                    <Btn
                                        color="#dc2626"
                                        onClick={() => remove(s)}
                                        disabled={s.usage_count > 0}
                                        title={s.usage_count > 0
                                            ? `서재 기록 ${s.usage_count}건에서 사용 중이라 삭제할 수 없습니다. 비활성화를 사용하세요.`
                                            : undefined}
                                    >
                                        삭제
                                    </Btn>
                                </div>
                            )}
                        </li>
    );


    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 860 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: FW.extraBold }}>구매처 관리</h2>

            {/* Add form */}
            <div style={{ background: COLOR.white, border: BORDER.lightAlt, borderRadius: RADIUS.md, padding: "20px 22px" }}>
                <div style={{ fontSize: 14, fontWeight: FW.bold, marginBottom: 12, color: COLOR.textDark }}>구매처 추가</div>
                <div style={{ display: "flex", gap: 8 }}>
                    <input
                        value={newCode}
                        onChange={e => setNewCode(e.target.value.toUpperCase())}
                        onKeyDown={e => e.key === "Enter" && add()}
                        placeholder="코드 (예: RIDI)"
                        maxLength={30}
                        style={{
                            width: 180, padding: "8px 12px", fontSize: 13,
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            border: BORDER.lightAlt, borderRadius: RADIUS.sm, outline: "none",
                        }}
                    />
                    <input
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && add()}
                        placeholder="표시 이름 (예: 리디)"
                        maxLength={50}
                        style={{
                            flex: 1, padding: "8px 12px", fontSize: 14,
                            border: BORDER.lightAlt, borderRadius: RADIUS.sm, outline: "none",
                        }}
                    />
                    <button
                        onClick={add}
                        disabled={adding || !newCode.trim() || !newName.trim()}
                        style={{
                            padding: "8px 18px", fontSize: 13, fontWeight: FW.bold,
                            background: adding || !newCode.trim() || !newName.trim() ? "#e4e4e7" : COLOR.textDark,
                            color: adding || !newCode.trim() || !newName.trim() ? COLOR.textLight : COLOR.white,
                            border: "none", borderRadius: RADIUS.sm,
                            cursor: adding || !newCode.trim() || !newName.trim() ? "default" : "pointer",
                            flexShrink: 0,
                        }}
                    >
                        추가
                    </button>
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: COLOR.textMuted }}>
                    코드는 영문 대문자·숫자·_ 만 쓸 수 있고 <b>생성 후에는 바꿀 수 없습니다.</b> 순서는 맨 뒤로 지정되며 수정에서 바꿀 수 있습니다.
                </div>
                {error && <div style={{ marginTop: 8, fontSize: 12, color: "#dc2626" }}>{error}</div>}
            </div>

            {/* Store list */}
            <div style={{ background: COLOR.white, border: BORDER.lightAlt, borderRadius: RADIUS.md, overflow: "hidden" }}>
                <div style={{ padding: "14px 22px", borderBottom: "1px solid #f4f4f5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 14, fontWeight: FW.bold, color: COLOR.textDark }}>구매처 목록</span>
                    <span style={{ fontSize: 12, color: COLOR.textMuted }}>
                        활성 {activeCount}개 / 전체 {stores.length}개 · 구매처 미지정 {unassigned}건
                    </span>
                </div>

                {loading ? (
                    <div style={{ padding: 24, textAlign: "center", color: COLOR.textLight, fontSize: 13 }}>불러오는 중...</div>
                ) : stores.length === 0 ? (
                    <div style={{ padding: 24, textAlign: "center", color: COLOR.textLight, fontSize: 13 }}>등록된 구매처가 없습니다.</div>
                ) : (
                    <>
                        <GroupHeader label="메인" count={primaryStores.length}
                                     hint="구매처 선택 시 기본으로 보입니다" />
                        {primaryStores.length === 0 ? (
                            <EmptyGroup text="메인 구매처가 없습니다. 최소 한 개는 올려두세요." />
                        ) : (
                            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                                {primaryStores.map((s, i) => renderRow(s, i === primaryStores.length - 1))}
                            </ul>
                        )}

                        <GroupHeader label="더보기" count={moreStores.length}
                                     hint="'더보기'를 눌렀을 때만 보입니다" />
                        {moreStores.length === 0 ? (
                            <EmptyGroup text="더보기로 내린 구매처가 없습니다." />
                        ) : (
                            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                                {moreStores.map((s, i) => renderRow(s, i === moreStores.length - 1))}
                            </ul>
                        )}
                    </>
                )}
            </div>

            <div style={{ fontSize: 12, color: COLOR.textMuted, lineHeight: 1.7 }}>
                · <b>비활성화</b>하면 앱의 구매처 선택 목록에서만 빠지고, 이미 그 구매처로 기록된 서재 항목은 그대로 남습니다.<br />
                · <b>삭제</b>는 아무도 쓰지 않은 구매처에만 가능합니다. 사용 중인 구매처를 지우면 해당 서재 기록의 구매처가 전부 사라지기 때문입니다.<br />
                · <b>순서</b>는 숫자가 작을수록 앞에 표시됩니다. 사이에 끼워 넣기 쉽도록 10 단위로 두는 것을 권장합니다.<br />
                · <b>메인 / 더보기</b>는 앱에서 구매처를 고를 때 기본으로 보일지, "더보기"를 눌러야 보일지를 정합니다.
                순서와는 별개이며, 각 그룹 안에서 위 순서대로 정렬됩니다.<br />
                · <b>비활성</b> 구매처는 메인이든 더보기든 앱에 아예 나오지 않습니다.<br />
                · <b>연동 앱 패키지명</b>을 넣으면 책 상세의 구매처 뱃지를 눌러 그 앱을 열 수 있습니다. 비워두면 뱃지는 표시 전용으로 남습니다.<br />
                · 단, 패키지명만 넣는다고 바로 열리지는 않습니다. <b>안드로이드 앱의 매니페스트에 해당 패키지가 등록돼 있어야</b> 하며,
                등록되지 않은 앱은 설치돼 있어도 "앱이 없습니다"로 뜹니다. 새 구매처를 연동할 때는 앱 개발자에게 함께 요청하세요.
            </div>
        </div>
    );
}

function GroupHeader({ label, count, hint }: { label: string; count: number; hint: string }) {
    return (
        <div style={{
            display: "flex", alignItems: "baseline", gap: 8,
            padding: "10px 22px", background: "#fafafa",
            borderTop: "1px solid #f4f4f5", borderBottom: "1px solid #f4f4f5",
        }}>
            <span style={{ fontSize: 13, fontWeight: FW.extraBold, color: COLOR.textDark }}>{label}</span>
            <span style={{ fontSize: 12, fontWeight: FW.bold, color: COLOR.textMuted }}>{count}개</span>
            <span style={{ fontSize: 11, color: COLOR.textLight }}>{hint}</span>
        </div>
    );
}

function EmptyGroup({ text }: { text: string }) {
    return (
        <div style={{ padding: "16px 22px", fontSize: 12, color: COLOR.textLight }}>{text}</div>
    );
}

function Btn({ children, onClick, color, disabled, title }: {
    children: React.ReactNode;
    onClick: () => void;
    color: string;
    disabled?: boolean;
    title?: string;
}) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            title={title}
            style={{
                padding: "5px 12px", fontSize: 12, fontWeight: FW.bold,
                background: "transparent",
                color: disabled ? "#d4d4d8" : color,
                border: `1px solid ${disabled ? "#e4e4e7" : color}`,
                borderRadius: RADIUS.xs,
                cursor: disabled ? "not-allowed" : "pointer",
            }}
        >
            {children}
        </button>
    );
}